// ── Game Settings ──
// Persistent settings store with localStorage backend.
// Manages camera mode (FPP/TPP), crosshair, graphics, audio, and related preferences.

import { type CrosshairConfig, DEFAULT_CROSSHAIR_CONFIG } from '../ui/crosshair-config';
import { ColorblindMode } from '../renderer/colorblind';
import { DEFAULT_SCOPE_SENSITIVITY } from '../input/aim-assist';

export type CameraMode = 'fpp' | 'tpp';
export type GraphicsQuality = 'low' | 'medium' | 'high' | 'ultra' | 'mobile-low' | 'mobile-medium' | 'mobile-high';
export type ControlMode = 'simple' | 'advanced' | 'custom';
/** UI color theme. 'auto' follows system preference (matchMedia). */
export type UiTheme = 'light' | 'dark' | 'auto';

/** Returns true if running on a mobile/touch device. */
export function isMobileDevice(): boolean {
  return typeof navigator !== 'undefined' && (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) || (navigator.maxTouchPoints > 0));
}

export interface GameSettings {
  cameraMode: CameraMode;
  /** TPP camera distance behind player (metres). */
  tppDistance: number;
  /** TPP camera height offset above eye level (metres). */
  tppHeight: number;
  /** TPP shoulder offset: positive = right, negative = left. */
  tppShoulder: number;
  /** Mouse sensitivity. */
  sensitivity: number;
  /** Selected skin ID for the player model. */
  selectedSkinId: number;
  /** Crosshair customization settings. */
  crosshair: CrosshairConfig;
  /** Graphics quality preset. */
  graphicsQuality: GraphicsQuality;
  /** Master volume 0-1. */
  masterVolume: number;
  /** SFX volume 0-1. */
  sfxVolume: number;
  /** Music volume 0-1. */
  musicVolume: number;
  /** Announcer / VO volume 0-1. */
  announcerVolume: number;
  /** Footstep volume 0-1. */
  footstepVolume: number;
  /** Touch input sensitivity (mobile only). */
  touchSensitivity: number;
  /** MOB-L2: Touch joystick deadzone (mobile only). 0..0.5, default 0.2. */
  touchJoystickDeadzone: number;
  /** Enable gyroscope aiming (mobile only). */
  gyroEnabled: boolean;
  /** Gyroscope sensitivity (mobile only). */
  gyroSensitivity: number;
  /** MOB-L3: Use accelerationIncludingGravity as a tilt-based fine-aim signal
   *  in addition to rotationRate. Off by default. */
  gyroUseAccelerometer: boolean;
  /** F-MOB-H1: Gyro rotationRate dead zone in degrees/sec. Default 1.5. */
  gyroDeadZone: number;
  /** F-MOB-H2: Joystick/look split percentage. Default 50, narrowed to 45 on
   *  very-narrow phones. Range 30..70. */
  touchSplitPercent: number;
  /** Aim assist strength 0-1 (mobile only). */
  aimAssistStrength: number;
  /** Enable ADS snap-to-target (mobile only). */
  adsSnapEnabled: boolean;
  /** Per-scope sensitivity multipliers (mobile only). */
  scopeSensitivity: Record<string, number>;
  /** Touch control layout mode (mobile only). */
  controlMode: ControlMode;
  /** Colorblind accessibility mode. */
  colorblindMode: ColorblindMode;
  /** Voice chat volume 0-1. */
  voiceVolume: number;
  /** Voice chat enabled. */
  voiceEnabled: boolean;
  /** BUG-P-16: selected microphone input device id (empty string = browser default).
   *  TODO: thread through to VoiceChatManager.acquireMicrophone — it currently
   *  does not accept a deviceId. */
  micDeviceId: string;
  /** UI color theme. Toggles body.theme-{light,dark,auto} class. Default 'dark'.
   *  Current UI ignores it; a future refactor will read primary/bg/text from
   *  CSS variables defined per theme. */
  theme: UiTheme;
}

const STORAGE_KEY = 'counter_pump_settings';
/** Exported for tests so a fresh SettingsStore can be diff-checked against persisted JSON. */
export const SETTINGS_STORAGE_KEY = STORAGE_KEY;

const DESKTOP_DEFAULTS: GameSettings = {
  cameraMode: 'fpp',
  tppDistance: 3.5,
  tppHeight: 1.2,
  tppShoulder: 0.5,
  sensitivity: 0.002,
  selectedSkinId: 0,
  crosshair: { ...DEFAULT_CROSSHAIR_CONFIG },
  graphicsQuality: 'high',
  masterVolume: 1.0,
  sfxVolume: 0.8,
  musicVolume: 0.3,
  announcerVolume: 1.0,
  footstepVolume: 0.7,
  touchSensitivity: 0.004,
  touchJoystickDeadzone: 0.2,
  gyroEnabled: false,
  gyroSensitivity: 0.015,
  gyroUseAccelerometer: false,
  gyroDeadZone: 1.5,
  touchSplitPercent: 50,
  aimAssistStrength: 0.7,
  adsSnapEnabled: true,
  scopeSensitivity: { ...DEFAULT_SCOPE_SENSITIVITY },
  controlMode: 'advanced',
  colorblindMode: ColorblindMode.None,
  voiceVolume: 0.7,
  voiceEnabled: true,
  micDeviceId: '',
  theme: 'dark',
};

const MOBILE_DEFAULTS: GameSettings = {
  ...DESKTOP_DEFAULTS,
  graphicsQuality: 'mobile-medium',
  touchSensitivity: 0.004,
  gyroEnabled: false,
  gyroSensitivity: 0.015,
  aimAssistStrength: 0.7,
  controlMode: 'advanced',
};

/**
 * Re-evaluates the mobile/desktop branch on every call so the default set is
 * not frozen at module-load time (which happens before any runtime env probe).
 */
function getDefaults(): GameSettings {
  return isMobileDevice() ? MOBILE_DEFAULTS : DESKTOP_DEFAULTS;
}

export class SettingsStore {
  private settings: GameSettings;
  private listeners: Array<(s: GameSettings) => void> = [];

  constructor() {
    this.settings = this.load();
  }

  get(): Readonly<GameSettings> {
    return this.settings;
  }

  /** Update one or more settings and persist. */
  update(partial: Partial<GameSettings>): void {
    // Clamp numeric fields to valid ranges
    if (partial.sensitivity !== undefined) partial.sensitivity = Math.max(0.0005, Math.min(0.01, partial.sensitivity));
    if (partial.masterVolume !== undefined) partial.masterVolume = Math.max(0, Math.min(1, partial.masterVolume));
    if (partial.sfxVolume !== undefined) partial.sfxVolume = Math.max(0, Math.min(1, partial.sfxVolume));
    if (partial.musicVolume !== undefined) partial.musicVolume = Math.max(0, Math.min(1, partial.musicVolume));
    if (partial.tppDistance !== undefined) partial.tppDistance = Math.max(1.5, Math.min(6, partial.tppDistance));
    if (partial.tppHeight !== undefined) partial.tppHeight = Math.max(0, Math.min(2.5, partial.tppHeight));
    if (partial.tppShoulder !== undefined) partial.tppShoulder = Math.max(-1, Math.min(1, partial.tppShoulder));
    if (partial.touchSensitivity !== undefined) partial.touchSensitivity = Math.max(0.001, Math.min(0.02, partial.touchSensitivity));
    if (partial.touchJoystickDeadzone !== undefined) partial.touchJoystickDeadzone = Math.max(0, Math.min(0.5, partial.touchJoystickDeadzone));
    if (partial.gyroSensitivity !== undefined) partial.gyroSensitivity = Math.max(0.001, Math.min(0.05, partial.gyroSensitivity));
    if (partial.gyroDeadZone !== undefined) partial.gyroDeadZone = Math.max(0, Math.min(10, partial.gyroDeadZone));
    if (partial.touchSplitPercent !== undefined) partial.touchSplitPercent = Math.max(30, Math.min(70, partial.touchSplitPercent));
    if (partial.aimAssistStrength !== undefined) partial.aimAssistStrength = Math.max(0, Math.min(1, partial.aimAssistStrength));
    if (partial.voiceVolume !== undefined) partial.voiceVolume = Math.max(0, Math.min(1, partial.voiceVolume));
    if (partial.announcerVolume !== undefined) partial.announcerVolume = Math.max(0, Math.min(1, partial.announcerVolume));
    if (partial.footstepVolume !== undefined) partial.footstepVolume = Math.max(0, Math.min(1, partial.footstepVolume));

    // Deep-merge crosshair to avoid clobbering unset fields
    if (partial.crosshair) {
      partial = { ...partial, crosshair: { ...this.settings.crosshair, ...partial.crosshair } };
    }
    Object.assign(this.settings, partial);
    this.save();
    for (const fn of this.listeners) fn(this.settings);
  }

  /** Toggle camera mode between FPP and TPP. */
  toggleCameraMode(): CameraMode {
    const next = this.settings.cameraMode === 'fpp' ? 'tpp' : 'fpp';
    this.update({ cameraMode: next });
    return next;
  }

  /** Reset all settings back to platform defaults (desktop/mobile). Persists
   *  to localStorage and notifies listeners. Used by the Reset to Defaults
   *  button in the settings menu. */
  reset(): void {
    this.settings = { ...getDefaults(), crosshair: { ...DEFAULT_CROSSHAIR_CONFIG } };
    this.save();
    for (const fn of this.listeners) fn(this.settings);
  }

  /** Register a listener called whenever settings change. */
  onChange(fn: (s: GameSettings) => void): void {
    this.listeners.push(fn);
  }

  /** Remove a previously registered listener. */
  offChange(fn: (s: GameSettings) => void): void {
    this.listeners = this.listeners.filter((f) => f !== fn);
  }

  private load(): GameSettings {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as Partial<GameSettings>;
        // Deep-merge crosshair config to handle partial saved data
        const crosshair = parsed.crosshair
          ? { ...DEFAULT_CROSSHAIR_CONFIG, ...parsed.crosshair }
          : { ...DEFAULT_CROSSHAIR_CONFIG };
        return { ...getDefaults(), ...parsed, crosshair };
      }
    } catch (e) {
      // Corrupted data — fall back to defaults
      console.warn('Settings corrupted, using defaults', e);
    }
    return { ...getDefaults(), crosshair: { ...DEFAULT_CROSSHAIR_CONFIG } };
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.settings));
    } catch {
      // localStorage full or unavailable — silently ignore
    }
  }
}

/** Singleton settings store. */
export const settingsStore = new SettingsStore();
